// Duration and Granularity types
export type Duration = "this_week" | "last_4_weeks" | "last_3_months" | "6_months" | "this_year" | "till_date";
export type Granularity = "daily" | "weekly" | "monthly";
export type RatingsFilterType = "quick" | "reflective";
export type ITimingGranularityType = "weekly" | "monthly";

export const GroupType = {
	All: "all",
	ZsUsers: "zs_users",
	ClientUsers: "client_users",
	ServiceAccounts: "service_accounts",
} as const;

export type GroupType = (typeof GroupType)[keyof typeof GroupType];

export const ActivityModuleType = {
	All: "all",
	Qualitative: "qualitative",
	Quantitative: "quantitative",
	AskKural: "askKural",
	ResearchNotes: "researchNotes",
	PXB: "pxb",
	WebBoards: "webBoards",
	Reports: "reports",
	Others: "others",
} as const;

export type ActivityModuleType = (typeof ActivityModuleType)[keyof typeof ActivityModuleType];

export interface IGroupOption {
	value: GroupType;
	label: string;
}

export const GROUP_OPTIONS: IGroupOption[] = [
	{ value: GroupType.All, label: "All" },
	{ value: GroupType.ZsUsers, label: "ZS Users" },
	{ value: GroupType.ClientUsers, label: "Client Users" },
	{ value: GroupType.ServiceAccounts, label: "Service Accounts" },
];

// Filter state
export interface FilterState {
	duration: Duration;
	granularity: Granularity;
	profile: string;
	group: GroupType;
	users: string | null;
}

export interface ExportFilters {
	timePeriod: Duration;
	granularity: string; // 'day' | 'week' | 'month'
	group: GroupType;
	userEmails: string[];
}

// ============ API RESPONSE INTERFACES (Real API Structure) ============

// Raw API user interaction data (with hyphens)
export interface APIUserInteractionData {
	date: string;
	// ── OLD FORMAT KEYS (kept for backward compat) ──
	"askKural-Reflective-TotalQueries": number;
	"askKural-Reflective-TotalChats": number;
	"askKural-TotalQueries": number;
	"askKural-TotalChats": number;
	"askKural-CombinedTotalQueries": number;
	"allStudies-TotalCreated": number;
	"askKural-ChatDeleted-Chat"?: number;
	"askKural-Reference-ViewTranscript"?: number;
	"askKural-Reference-ViewReport"?: number;
	"askKural-Reference-ViewReference-Reference"?: number;
	"askKural-Widget-CountsWidget"?: number;
	"askKural-Widget-GenerateWidgetLabels-Widget"?: number;
	"askKural-Widget-WidgetExport-Widget"?: number;
	"askKural-QueryCount-StopGeneration-Chat"?: number;
	"fileUpload-Transcript"?: number;
	"fileUpload-Survey"?: number;
	"fileUpload-RawData"?: number;
	"fileUpload-DiscussionGuide"?: number;
	"fileUpload-Report"?: number;
	"fileUpload-Metadata"?: number;
	"fileUpload-Notes"?: number;
	"fileUpload-AnalysisConfig"?: number;
	"fileUpload-ChartConfig"?: number;
	"fileUpload-Adm"?: number;
	"fileDownload-Transcript"?: number;
	"fileDownload-DiscussionGuide"?: number;
	"fileDownload-Survey"?: number;
	"fileDownload-RawData"?: number;
	"fileDownload-Notes"?: number;
	"fileDownload-Metadata"?: number;
	"fileDownload-Report"?: number;
	"fileDelete-Transcript"?: number;
	"fileDelete-DiscussionGuide"?: number;
	"fileDelete-Survey"?: number;
	"fileDelete-RawData"?: number;
	"fileDelete-Notes"?: number;
	"fileDelete-Report"?: number;
	"fileDelete-Adm"?: number;
	"fileDelete-AnalysisConfig"?: number;
	"fileDelete-ChartConfig"?: number;
	"viewTranscript-Transcript"?: number;
	"viewDiscussionGuide-DiscussionGuide"?: number;
	"autoGenerateDiscussionGuide-DiscussionGuide"?: number;
	"quantitativeMarketResearch-StudiesCreated"?: number;
	"qualitativeMarketResearch-StudiesCreated"?: number;
	"qualitativeMarketResearch-InsightsViewed"?: number;
	"qualitativeMarketResearch-InsightsUpdated-Insights"?: number;
	"qualitativeMarketResearch-InsightsUpdated-DeleteInsightQuestion-Answer"?: number;
	"qualitativeMarketResearch-Insights-ViewAnswer-Answer"?: number;
	"qualitativeMarketResearch-Insights-GenerateInsights-Insights"?: number;
	"qualitativeMarketResearch-Insights-DownloadInsights-Insights"?: number;
	"qualitativeMarketResearch-Insights-DownloadAnswer-Answer"?: number;
	"quantitativeMarketResearch-Dashboard-SearchCharts-Dashboard"?: number;
	"quantitativeMarketResearch-Dashboard-ViewExecutionalChart-Dashboard"?: number;
	"quantitativeMarketResearch-Dashboard-ViewInsights-Insights"?: number;
	"quantitativeMarketResearch-Dashboard-GenerateStatMarkers-Dashboard"?: number;
	"quantitativeMarketResearch-Dashboard-DashboardExport-Dashboard"?: number;
	"quantitativeMarketResearch-Dashboard-PPTExport-PPTExport"?: number;
	"quantitativeMarketResearch-Insights-GenerateInsights-Insights"?: number;
	"quantitativeMarketResearch-ManageStudies-AutoGenerateADM-Adm"?: number;
	"quantitativeMarketResearch-Segments-ViewSegments-Segment"?: number;
	"quantitativeMarketResearch-Segments-CreateSegments"?: number;
	"quantitativeMarketResearch-Segments-EditSegments"?: number;
	"quantitativeMarketResearch-Segments-DeleteSegments"?: number;
	"quantitativeMarketResearch-StudiesUpdated-Study"?: number;
	"researchNotes-StudiesCreated"?: number;
	"researchNotes-Insights-ViewReferences-Notes"?: number;
	"researchNotes-Insights-GenerateSummary-Notes"?: number;
	"researchNotes-ManageStudies-ViewNotes-Notes"?: number;
	"researchNotes-ManageStudies-ColumnMappingUpdated-Notes"?: number;
	"researchNotes-ManageStudies-FileDelete-Notes"?: number;
	"researchNotes-ManageStudies-FileDownload-Notes"?: number;
	"reports-StudiesCreated"?: number;
	"reports-ManageStudies-ViewReport"?: number;
	"webBoards-StudiesCreated"?: number;
	"webBoards-Insights-ViewReferences-WebBoards"?: number;
	"webBoards-Insights-GenerateSummary-WebBoards"?: number;
	"studies-TaxonomyLinked-WebBoards"?: number;
	"studies-TaxonomyLinked-Notes"?: number;
	"studies-AccessUpdated"?: number;
	"studies-StudiesCreated-Study"?: number;
	"studies-StudiesUpdated-Study"?: number;
	"studies-StudiesDeleted"?: number;
	"studies-Starred"?: number;
	"manageStudies-GenerateInsights"?: number;
	"manageStudies-FileDelete"?: number;
	"manageStudies-FileDownload"?: number;
	"taxonomy-TaxonomyViewed-Taxonomy"?: number;
	"taxonomy-TaxonomyDownloaded-Taxonomy"?: number;
	"taxonomy-TaxonomyCreated-Taxonomy"?: number;
	"taxonomy-TaxonomyUpdated-Taxonomy"?: number;
	"taxonomy-TaxonomyDeleted-Taxonomy"?: number;
	"folders-FolderCreated-Folder"?: number;
	"folders-FolderDeleted-Folder"?: number;
	"folders-StudyMovedToFolder-Folder"?: number;

	// ── NEW FORMAT KEYS (priority in transformer) ──
	"askKural-totalQueries"?: number;
	"askKural-totalChats"?: number;
	"askKural-reflective-TotalQueries"?: number;
	"askKural-reflective-TotalChats"?: number;
	"askKural-chatDeleted"?: number;
	"askKural-reference-viewTranscript"?: number;
	"askKural-reference-viewReport"?: number;
	"askKural-reference-viewReference"?: number;
	"askKural-widget-countsWidget"?: number;
	"askKural-widget-generateWidgetLabels"?: number;
	"askKural-widget-widgetExport"?: number;
	"askKural-totalQueries-stopGeneration"?: number;
	"qualitativeMarketResearch-insightsViewed"?: number;
	"qualitativeMarketResearch-insightsUpdated"?: number;
	"qualitativeMarketResearch-insightsUpdated-deleteInsightQuestion"?: number;
	"qualitativeMarketResearch-insights-viewAnswer"?: number;
	"qualitativeMarketResearch-insights-generateInsights"?: number;
	"qualitativeMarketResearch-insights-downloadInsights"?: number;
	"qualitativeMarketResearch-insights-downloadAnswer"?: number;
	"qualitativeMarketResearch-manageStudies-autoGenerateDiscussionGuide"?: number;
	"qualitativeMarketResearch-manageStudies-viewTranscript"?: number;
	"qualitativeMarketResearch-manageStudies-viewDiscussionGuide"?: number;
	"qualitativeMarketResearch-manageStudies-fileUpload-transcript"?: number;
	"qualitativeMarketResearch-manageStudies-fileUpload-discussionGuide"?: number;
	"qualitativeMarketResearch-manageStudies-fileUpload-metadata"?: number;
	"qualitativeMarketResearch-manageStudies-fileDownload-transcript"?: number;
	"qualitativeMarketResearch-manageStudies-fileDownload-discussionGuide"?: number;
	"qualitativeMarketResearch-manageStudies-fileDownload-metadata"?: number;
	"qualitativeMarketResearch-manageStudies-fileDelete-transcript"?: number;
	"qualitativeMarketResearch-manageStudies-fileDelete-discussionGuide"?: number;
	"qualitativeMarketResearch-manageStudies-fileDelete-metadata"?: number;
	"quantitativeMarketResearch-dashboard-searchCharts"?: number;
	"quantitativeMarketResearch-dashboard-viewExecutionalChart"?: number;
	"quantitativeMarketResearch-dashboard-viewInsights"?: number;
	"quantitativeMarketResearch-dashboard-generateStatMarkers"?: number;
	"quantitativeMarketResearch-dashboard-dashboardExport"?: number;
	"quantitativeMarketResearch-dashboard-pPTExport"?: number;
	"quantitativeMarketResearch-insights-generateInsights"?: number;
	"quantitativeMarketResearch-manageStudies-autoGenerateAdm"?: number;
	"quantitativeMarketResearch-manageStudies-fileUpload-survey"?: number;
	"quantitativeMarketResearch-manageStudies-fileUpload-rawData"?: number;
	"quantitativeMarketResearch-manageStudies-fileUpload-adm"?: number;
	"quantitativeMarketResearch-manageStudies-fileUpload-analysisConfig"?: number;
	"quantitativeMarketResearch-manageStudies-fileUpload-chartConfig"?: number;
	"quantitativeMarketResearch-manageStudies-fileDownload-survey"?: number;
	"quantitativeMarketResearch-manageStudies-fileDownload-rawData"?: number;
	"quantitativeMarketResearch-manageStudies-fileDownload-adm"?: number;
	"quantitativeMarketResearch-manageStudies-fileDownload-analysisConfig"?: number;
	"quantitativeMarketResearch-manageStudies-fileDownload-chartConfig"?: number;
	"quantitativeMarketResearch-manageStudies-fileDelete-survey"?: number;
	"quantitativeMarketResearch-manageStudies-fileDelete-rawData"?: number;
	"quantitativeMarketResearch-manageStudies-fileDelete-adm"?: number;
	"quantitativeMarketResearch-manageStudies-fileDelete-analysisConfig"?: number;
	"quantitativeMarketResearch-manageStudies-fileDelete-chartConfig"?: number;
	"quantitativeMarketResearch-segments-viewSegment"?: number;
	"quantitativeMarketResearch-segments-createSegment"?: number;
	"quantitativeMarketResearch-segments-editSegment"?: number;
	"quantitativeMarketResearch-segments-deleteSegment"?: number;
	"quantitativeMarketResearch-studiesUpdated"?: number;
	"researchNotes-insights-viewReferences"?: number;
	"researchNotes-insights-generateSummary"?: number;
	"researchNotes-manageStudies-viewNotes"?: number;
	"researchNotes-manageStudies-columnMappingUpdated"?: number;
	"researchNotes-manageStudies-fileDelete-notes"?: number;
	"researchNotes-manageStudies-fileDownload-notes"?: number;
	"researchNotes-manageStudies-fileUpload-notes"?: number;
	"researchNotes-taxonomyLinked--notes"?: number;
	"reports-manageStudies-viewReport"?: number;
	"reports-manageStudies-fileUpload-report"?: number;
	"reports-manageStudies-fileDownload-report"?: number;
	"reports-manageStudies-fileDelete-report"?: number;
	"webBoards-insights-viewReferences"?: number;
	"webBoards-insights-generateSummary"?: number;
	"webBoards-taxonomyLinked--webBoards"?: number;
	"studies-accessUpdated"?: number;
	"studies-studiesCreated"?: number;
	"studies-studiesUpdated"?: number;
	"studies-studiesDeleted"?: number;
	"studies-starred"?: number;
	"studies-taxonomyLink"?: number;
	"taxonomy-taxonomyViewed"?: number;
	"taxonomy-taxonomyDownloaded"?: number;
	"taxonomy-taxonomyViewed-downloadTaxonomy"?: number;
	"taxonomy-taxonomyCreated"?: number;
	"taxonomy-taxonomyUpdated"?: number;
	"taxonomy-taxonomyDeleted"?: number;
	"folders-folderCreated"?: number;
	"folders-folderDeleted"?: number;
	"folders-studyMovedToFolder"?: number;
	"qualitativeMarketResearch-studiesCreated"?: number;
	"quantitativeMarketResearch-studiesCreated"?: number;
	"reports-studiesCreated"?: number;
	"researchNotes-studiesCreated"?: number;
	// ── PXB FORMAT KEYS ──
	"pXB-segments-createSegment"?: number;
	"pXB-segments-viewSegment"?: number;
	"pXB-dashboard-pPTExport"?: number;
	"pXB-dashboard-filterCharts"?: number;
	"pXB-dashboard-dashboardExport"?: number;
	"pXB-manageStudies-autoGenerateADM"?: number;
	"pXB-insights-generateInsights"?: number;
	"pXB-studiesUpdated"?: number;
	"pXB-manageStudies-fileDownload-survey"?: number;
	"pXB-manageStudies-fileDownload-rawData"?: number;
	"pXB-manageStudies-fileDownload-adm"?: number;
	"pXB-manageStudies-fileDelete-chartConfig"?: number;
	// ── NEW: Quantitative additions ──
	"quantitativeMarketResearch-segments-upsertSegment"?: number;
	"quantitativeMarketResearch-dashboard-compareCharts"?: number;
	"quantitativeMarketResearch-dashboard-viewSectionalChart"?: number;
	"quantitativeMarketResearch-dashboard-viewCharts"?: number;

	// ── NEW: PXB additions ──
	"pXB-segments-editSegment"?: number;
	"pXB-segments-deleteSegment"?: number;
	"pXB-segments-upsertSegment"?: number;
	"pXB-segments-viewSegments"?: number;
	"pXB-dashboard-compareCharts"?: number;
	"pXB-dashboard-viewSectionalChart"?: number;
	"pXB-dashboard-viewCharts"?: number;
	"pXB-dashboard-searchCharts"?: number;
	"pXB-dashboard-viewExecutionalChart"?: number;
	"pXB-dashboard-viewInsights"?: number;
	"pXB-dashboard-generateStatMarkers"?: number;
	"pXB-manageStudies-fileDownload"?: number;
	"pXB-manageStudies-fileDelete"?: number;

	// ── NEW: Ask Kural additions ──
	"askKural-widget-quantWidget"?: number;
	"askKural-widget-quotesWidget"?: number;
	"askKural-reference-viewWidgetReference"?: number;
	"askKural-reference-viewFile-transcript"?: number;
	"askKural-reference-viewFile-report"?: number;

	// ── NEW: Research Notes additions ──
	"researchNotes-studiesUpdated"?: number;
	"researchNotes-insights-viewInsights"?: number;
	"researchNotes-taxonomyLinked"?: number;

	// ── NEW: Qualitative additions ──
	"qualitativeMarketResearch-insights-updateInsights"?: number;
	"qualitativeMarketResearch-insights-editInsightTag"?: number;
	"qualitativeMarketResearch-insights-viewReferences"?: number;
	"qualitativeMarketResearch-manageStudies-editDiscussionGuide"?: number;
	"qualitativeMarketResearch-manageStudies-editTranscript"?: number;
	"qualitativeMarketResearch-manageStudies-autoGenerateDG"?: number;
	"qualitativeMarketResearch-accessUpdated"?: number;
}

// Raw API screen time data
export interface APIScreenTimeData {
	date: string;
	askKural: number;
	studies: number;
	deepDive: number;
	addStudy: number;
	manageStudies: number;
}

// Raw API granularity data block
export interface APIGranularityData {
	metadata: {
		timePeriod: string;
		granularity: string;
		profileFilter: string;
		moduleFilter: string;
		userEmailFilter: string;
		dateRange: { start: string; end: string };
		documentsScanned: number;
		collectionUsed: string;
	};
	userInteractions: APIUserInteractionData[];
	screenTime: APIScreenTimeData[];
	askKuralRatings: RatingsData[];
	reflectiveModeRatings: RatingsData[];
	feedbackRatings: RatingsData[];
	userActivity: APIUserActivityData[];
}

// Raw API dashboard response
export interface APIDashboardResponse {
	metadata: {
		timePeriod: string;
		profileFilter: string;
		moduleFilter: string;
		userEmailFilter: string;
		availableGranularities: string[];
		queryTimeMs: number;
		requestedAt: string;
	};
	data: {
		day: APIGranularityData;
		week: APIGranularityData;
		month?: APIGranularityData;
	};
}

export interface APIUserActivityData {
	date: string;
	activeZsUsers: number;
	inactiveZsUsers: number;
	activeClients: number;
	inactiveClients: number;
	activeServiceAccounts?: number;
	inactiveServiceAccounts?: number;
	zsUsersByJobLevel?: ZsUserByJobLevel[];
}

// Raw API KPI response
export interface APIKPIResponse {
	kpis: APIKPIItem[];
	metadata: {
		time_period: string;
		query_time_ms: number;
		requested_at: string;
		calculation_method: string;
		filters_applied: {
			start_date: string;
			end_date: string;
			profile: string | null;
			user_email: string | null;
		};
	};
}

// ============ NORMALIZED INTERFACES (Used in components) ============

// KPI data (normalized from API)
export interface KPIData {
	chatsWithFollowup: number;
	usersUsedFollowup: number;
	ratingsCount: number;
	ratingsAbove3: number;
	averageRating: number;
	uniqueProfiles: number;
	activeStudies: number;
	studiesWithDG: number;
	studiesWithTR: number;
	totalTRsUploaded: number;
	studiesWithInsights: number;
	studiesWithMetadata: number;
	usersWithEditAccess: number;
	activeReportStudies: number;
	studiesWithSurveys: number;
	studiesWithRawData: number;
	studiesWithADM: number;
	studiesWithAnalysisConfig: number;
	studiesWithChartConfig: number;
	studiesWithNotes: number;
	totalDGsUploaded: number;
	totalMetadataUploaded: number;
	totalSurveysUploaded: number;
	totalRawDataUploaded: number;
	totalADMUploaded: number;
	totalACConfigs: number;
	totalCCConfigs: number;
	totalNotesUploaded: number;
	totalCombinedUploads: number;
	maxUploadType: string;
	percentWithDG: string;
	percentWithTR: string;
	percentWithMetadata: string;
	percentWithInsights: string;
	percentQualWithInsights: string;
	percentQuantWithInsights: string;
	percentNotesWithInsights: string;
	descQualWithInsights: string;
	descQuantWithInsights: string;
	descNotesWithInsights: string;
	percentWithReports: string;
	percentWithSurveys: string;
	percentWithRawData: string;
	percentWithADM: string;
	percentWithAnalysisConfig: string;
	percentWithChartConfig: string;
	percentWithNotes: string;
	descWithDG: string;
	descWithTR: string;
	descWithMetadata: string;
	descWithInsights: string;
	descWithReports: string;
	descWithSurveys: string;
	descWithRawData: string;
	descWithADM: string;
	descWithAnalysisConfig: string;
	descWithChartConfig: string;
	descWithNotes: string;
	totalAskKuralQueries: number;
	descActiveStudies: string;
	descChatsWithFollowup: string;
	descUsersUsedFollowup: string;
	descRatingsCount: string;
	descRatingsAbove3: string;
	descAverageRating: string;
	descUniqueProfiles: string;
	descTotalCombinedUploads: string;
	descMaxUploadType: string;
	descUsersWithEditAccess: string;
	descStudiesWithInsights: string;
	descTotalAskKuralQueries: string;
}

export interface APIKPIItem {
	title: string;
	value: number;
	description?: string; // Add optional description field
	trend: number | null;
	trendDirection: string | null;
}

export interface UserActivityData {
	date: string;
	activeZsUsers: number;
	inactiveZsUsers: number;
	activeClients: number;
	inactiveClients: number;
	activeServiceAccounts: number;
	inactiveServiceAccounts: number;
	zsUsersByJobLevel?: ZsUserByJobLevel[];
}

// Normalized user interaction metrics (used in components)
export interface UserInteractionData {
	date: string;
	askKuralReflectiveQueries: number;
	askKuralTotalQueries: number;
	fileUploadTranscript: number;
	fileUploadSurvey: number;
	fileUploadRawData: number;
	fileUploadDG: number;
	fileUploadReport: number;
	fileUploadMetadata: number;
	fileUploadNotes: number;
	quantStudiesCreated: number;
	qualStudiesCreated: number;
	generateInsights: number;
	reportsStudiesCreated: number;
	researchNotesStudiesCreated: number;
	autoGenerateDG: number;
	fileDeleteAdm: number;
	fileDeleteAnalysisConfig: number;
	transcriptDownloads: number;
	viewTranscript: number;
	insightsViewed: number;
	studiesDeleted: number;
	reportDownloads: number;
	fileDeleteRawData: number;
	fileDeleteTranscript: number;
	askKuralCombinedQueries: number;
	allStudiesTotalCreated: number;
	// New ask kural fields
	askKuralTotalChats: number;
	askKuralReflectiveTotalChats: number;
	askKuralChatDeleted: number;
	askKuralReferenceViewTranscript: number;
	askKuralReferenceViewReport: number;
	askKuralReferenceViewReference: number;
	askKuralWidgetCounts: number;
	askKuralWidgetGenerateLabels: number;
	askKuralWidgetExport: number;
	askKuralStopGeneration: number;

	// New qualitative fields
	qualViewAnswer: number;
	qualGenerateInsights: number;
	qualDownloadInsights: number;
	qualDownloadAnswer: number;
	qualInsightsUpdated: number;
	qualDeleteInsightQuestion: number;
	fileDownloadTranscript: number;
	fileDownloadDiscussionGuide: number;
	fileDeleteDiscussionGuide: number;

	// New quantitative fields
	quantStudiesUpdated: number;
	quantSegmentsViewed: number;
	quantSegmentsCreated: number;
	quantSegmentsEdited: number;
	quantSegmentsDeleted: number;
	quantDashboardSearchCharts: number;
	quantDashboardViewExecutionalChart: number;
	quantDashboardViewInsights: number;
	quantDashboardGenerateStatMarkers: number;
	quantDashboardExport: number;
	quantDashboardPPTExport: number;
	quantGenerateInsights: number;
	quantAutoGenerateADM: number;
	fileUploadAnalysisConfig: number;
	fileUploadChartConfig: number;
	fileUploadAdm: number;
	fileDownloadSurvey: number;
	fileDownloadRawData: number;
	fileDeleteSurvey: number;
	fileDeleteDiscussionGuide: number;
	fileDeleteChartConfig: number;

	// New research notes fields
	notesViewReferences: number;
	notesViewNotes: number;
	notesGenerateSummary: number;
	notesColumnMappingUpdated: number;
	fileDownloadNotes: number;
	fileDeleteNotes: number;

	// New web boards fields
	webBoardsStudiesCreated: number;
	webBoardsViewReferences: number;
	webBoardsGenerateSummary: number;
	studiesTaxonomyLinkedWebBoards: number;

	// New reports fields
	reportsViewReport: number;
	fileDownloadMetadata: number;
	fileDeleteReport: number;

	// New others fields
	studiesAccessUpdated: number;
	studiesStarred: number;
	studiesCreatedStudy: number;
	studiesUpdatedStudy: number;
	studiesTaxonomyLinkedNotes: number;
	manageStudiesFileDelete: number;
	manageStudiesFileDownload: number;
	taxonomyViewed: number;
	taxonomyDownloaded: number;
	taxonomyCreated: number;
	taxonomyUpdated: number;
	taxonomyDeleted: number;
	folderCreated: number;
	folderDeleted: number;
	studyMovedToFolder: number;

	// PXB fields
	pxbStudiesUpdated: number;
	pxbSegmentsViewed: number;
	pxbSegmentsCreated: number;
	pxbDashboardFilterCharts: number;
	pxbDashboardExport: number;
	pxbDashboardPPTExport: number;
	pxbGenerateInsights: number;
	pxbAutoGenerateADM: number;
	pxbFileDownloadSurvey: number;
	pxbFileDownloadRawData: number;
	pxbFileDownloadAdm: number;
	pxbFileDeleteChartConfig: number;

	// ── New Quantitative ──
	quantSegmentsUpsert: number;
	quantDashboardCompareCharts: number;
	quantDashboardViewSectionalChart: number;
	quantDashboardViewCharts: number;

	// ── New PXB ──
	pxbSegmentsEdited: number;
	pxbSegmentsDeleted: number;
	pxbSegmentsUpsert: number;
	pxbDashboardCompareCharts: number;
	pxbDashboardViewSectionalChart: number;
	pxbDashboardViewCharts: number;
	pxbDashboardSearchCharts: number;
	pxbDashboardViewExecutionalChart: number;
	pxbDashboardViewInsights: number;
	pxbDashboardGenerateStatMarkers: number;
	pxbFileDownload: number;
	pxbFileDelete: number;

	// ── New Ask Kural ──
	askKuralWidgetQuant: number;
	askKuralWidgetQuotes: number;
	askKuralReferenceViewWidgetReference: number;
	askKuralReferenceViewFileTranscript: number;
	askKuralReferenceViewFileReport: number;

	// ── New Research Notes ──
	researchNotesStudiesUpdated: number;
	notesViewInsights: number;
	studiesTaxonomyLinked: number;

	// ── New Qualitative ──
	qualUpdateInsights: number;
	qualEditInsightTag: number;
	qualViewReferences: number;
	qualEditDiscussionGuide: number;
	qualEditTranscript: number;
	qualViewDiscussionGuide: number;
	qualAutoGenerateDGNew: number;
	qualAccessUpdated: number;
}

// Ratings distribution
export interface RatingsData {
	date: string;
	star5: number;
	star4: number;
	star3: number;
	star2: number;
	star1: number;
}

// Normalized user login data
export interface UserLoginData {
	date: string;
	zsUsers: number;
	nonZsUsers: number;
	totalUsers: number;
}

// Screen time data (normalized)
export interface ScreenTimeData {
	date: string;
	askKural: number;
	studies: number;
	deepDive: number;
	addStudy: number;
	manageStudies: number;
}

// Normalized dashboard data (used in components)
export interface DashboardData2 {
	metadata: {
		startDate: string;
		endDate: string;
		granularity: Granularity;
		profileFilter: string;
	};
	userInteractions: UserInteractionData[];
	askKuralRatings: RatingsData[];
	reflectiveModeRatings: RatingsData[];
	feedbackRatings: RatingsData[];
	userActivity: UserActivityData[];
	screenTime: ScreenTimeData[];
}

// KPI data wrapper
export interface DashboardData3 {
	kpis: KPIData;
}

export const PASTEL_COLORS: Record<string, string> = {
	blue_1: "#C0D7F5",
	blue_2: "#8FBAF3",
	blue_3: "#6CA2E9",
	blue_4: "#3C7ED4",
	blue_5: "#2956AC",
	blue_6: "#2F4B7F",
	gray: "#e5e7eb",
};

// Ordered array for easy index-based access
export const PASTEL_PALETTE = Object.values(PASTEL_COLORS);

// Chart color palette
export const CHART_COLORS = {
	primary: "#1e40af",
	secondary: "#3b82f6",
	tertiary: "#60a5fa",
	success: "#10b981",
	warning: "#f59e0b",
	danger: "#ef4444",
	purple: "#8b5cf6",
	pink: "#ec4899",
	teal: "#14b8a6",
	orange: "#f97316",
	slate: "#64748b",
	star5: "#10b981",
	star4: "#22c55e",
	star3: "#eab308",
	star2: "#f97316",
	star1: "#ef4444",
};

export const SERIES_COLORS: string[] = [
	"#76C2AC",
	"#4B83DC",
	"#A55C28",
	"#EA39F3",
	"#2EB8BA",
	"#65249C",
	"#809C41",
	"#65D9FF",
	"#714EAD",
	"#862778",
	"#1F7197",
	"#C089E7",
	"#56B0DA",
	"#9939F3",
];

export const DURATION_OPTIONS: { value: Duration; label: string; description: string }[] = [
	{ value: "this_week", label: "This Week", description: "From Monday up to yesterday" },
	{ value: "last_4_weeks", label: "Last 4 Weeks", description: "Past 4 weeks, excluding the current week" },
	{ value: "last_3_months", label: "Last 3 Months", description: "Past 13 weeks, excluding the current week" },
	{ value: "6_months", label: "6 Months", description: "Past 6 months, excluding the current month" },
	{ value: "this_year", label: "This Year", description: "From January 1 up to the end of last month" },
	{ value: "till_date", label: "Till Date", description: "All data available up to the end of last month" },
];

// Granularity rules
export const GRANULARITY_RULES: Record<Duration, Granularity[]> = {
	this_week: ["daily"],
	last_4_weeks: ["daily", "weekly"],
	last_3_months: ["weekly"],
	"6_months": ["monthly"],
	this_year: ["monthly"],
	till_date: ["monthly"],
};

// ============ OTHER INTERFACES ============
export interface ProfileData {
	profile_name: string;
	display_profile_name: string;
	primary_language: string;
	secondary_language: string[];
	whitelisted_users: Array<{
		_id: string;
		collaborator_id: string;
		collaborator_name: string;
		collaborator_email: string;
		user_access_type: string;
	}>;
	isActive: boolean;
}

export interface UserData {
	_id: string;
	collaborator_id: string;
	collaborator_name: string;
	collaborator_email: string;
	email?: string;
	name?: string;
	enable_access?: boolean;
}

export interface ProfilesResponse {
	_id: string;
	message: string;
	data: ProfileData[];
}

export interface UsersResponse {
	data: UserData[];
	total: number;
	page: number;
	page_size: number;
}

export interface TooltipParam {
	axisValue?: string;
	dataIndex?: number;
	marker?: string;
	seriesName?: string;
	value?: number;
	name?: string;
	data?: unknown;
}

export interface ActivityTotals {
	// ── existing ──
	reflectiveQueries: number;
	askKuralQueries: number;
	transcripts: number;
	surveys: number;
	rawData: number;
	discussionGuides: number;
	reports: number;
	metadata: number;
	notes: number;
	quantStudies: number;
	qualStudies: number;
	reportStudies: number;
	researchNotesStudies: number;
	generateInsights: number;
	autoGenerateDG: number;
	fileDeleteAdm: number;
	fileDeleteAnalysisConfig: number;
	transcriptDownloads: number;
	viewTranscript: number;
	insightsViewed: number;
	studiesDeleted: number;
	reportDownloads: number;
	fileDeleteRawData: number;
	fileDeleteTranscript: number;

	// ── ask kural ──
	askKuralTotalChats: number;
	askKuralReflectiveTotalChats: number;
	askKuralChatDeleted: number;
	askKuralReferenceViewTranscript: number;
	askKuralReferenceViewReport: number;
	askKuralReferenceViewReference: number;
	askKuralWidgetCounts: number;
	askKuralWidgetGenerateLabels: number;
	askKuralWidgetExport: number;
	askKuralStopGeneration: number;

	// ── qualitative ──
	qualViewAnswer: number;
	qualGenerateInsights: number;
	qualDownloadInsights: number;
	qualDownloadAnswer: number;
	qualInsightsUpdated: number;
	qualDeleteInsightQuestion: number;
	fileDownloadTranscript: number;
	fileDownloadDiscussionGuide: number;
	fileDeleteDiscussionGuide: number;

	// ── quantitative ──
	quantStudiesUpdated: number;
	quantSegmentsViewed: number;
	quantSegmentsCreated: number;
	quantSegmentsEdited: number;
	quantSegmentsDeleted: number;
	quantDashboardSearchCharts: number;
	quantDashboardViewExecutionalChart: number;
	quantDashboardViewInsights: number;
	quantDashboardGenerateStatMarkers: number;
	quantDashboardExport: number;
	quantDashboardPPTExport: number;
	quantGenerateInsights: number;
	quantAutoGenerateADM: number;
	fileUploadAnalysisConfig: number;
	fileUploadChartConfig: number;
	fileUploadAdm: number;
	fileDownloadSurvey: number;
	fileDownloadRawData: number;
	fileDeleteSurvey: number;
	fileDeleteDiscussionGuide: number;
	fileDeleteChartConfig: number;

	// ── research notes ──
	notesViewReferences: number;
	notesViewNotes: number;
	notesGenerateSummary: number;
	notesColumnMappingUpdated: number;
	fileDownloadNotes: number;
	fileDeleteNotes: number;

	// ── web boards ──
	webBoardsStudiesCreated: number;
	webBoardsViewReferences: number;
	webBoardsGenerateSummary: number;
	studiesTaxonomyLinkedWebBoards: number;

	// ── reports ──
	reportsViewReport: number;
	fileDownloadMetadata: number;
	fileDeleteReport: number;

	// ── others ──
	studiesAccessUpdated: number;
	studiesStarred: number;
	studiesCreatedStudy: number;
	studiesUpdatedStudy: number;
	studiesTaxonomyLinkedNotes: number;
	manageStudiesFileDelete: number;
	manageStudiesFileDownload: number;
	taxonomyViewed: number;
	taxonomyDownloaded: number;
	taxonomyCreated: number;
	taxonomyUpdated: number;
	taxonomyDeleted: number;
	folderCreated: number;
	folderDeleted: number;
	studyMovedToFolder: number;

	// ── pxb ──
	pxbStudiesUpdated: number;
	pxbSegmentsViewed: number;
	pxbSegmentsCreated: number;
	pxbDashboardFilterCharts: number;
	pxbDashboardExport: number;
	pxbDashboardPPTExport: number;
	pxbGenerateInsights: number;
	pxbAutoGenerateADM: number;
	pxbFileDownloadSurvey: number;
	pxbFileDownloadRawData: number;
	pxbFileDownloadAdm: number;
	pxbFileDeleteChartConfig: number;

	// ── New Quantitative ──
	quantSegmentsUpsert: number;
	quantDashboardCompareCharts: number;
	quantDashboardViewSectionalChart: number;
	quantDashboardViewCharts: number;

	// ── New PXB ──
	pxbSegmentsEdited: number;
	pxbSegmentsDeleted: number;
	pxbSegmentsUpsert: number;
	pxbDashboardCompareCharts: number;
	pxbDashboardViewSectionalChart: number;
	pxbDashboardViewCharts: number;
	pxbDashboardSearchCharts: number;
	pxbDashboardViewExecutionalChart: number;
	pxbDashboardViewInsights: number;
	pxbDashboardGenerateStatMarkers: number;
	pxbFileDownload: number;
	pxbFileDelete: number;

	// ── New Ask Kural ──
	askKuralWidgetQuant: number;
	askKuralWidgetQuotes: number;
	askKuralReferenceViewWidgetReference: number;
	askKuralReferenceViewFileTranscript: number;
	askKuralReferenceViewFileReport: number;

	// ── New Research Notes ──
	researchNotesStudiesUpdated: number;
	notesViewInsights: number;
	studiesTaxonomyLinked: number;

	// ── New Qualitative ──
	qualUpdateInsights: number;
	qualEditInsightTag: number;
	qualViewReferences: number;
	qualEditDiscussionGuide: number;
	qualEditTranscript: number;
	qualViewDiscussionGuide: number;
	qualAutoGenerateDGNew: number;
	qualAccessUpdated: number;
}

export const DEFAULT_FILTERS: FilterState = {
	duration: "last_4_weeks",
	granularity: "daily",
	profile: "all",
	group: GroupType.All,
	users: null,
};

export const API_ENDPOINTS = {
	dashboardData: "/api/v1/monitoring/dashboard",
	profiles: "/admin/profiles",
	usersPaginated: "/admin/users/paginated",
	usersSearch: "/admin/users/search",
	exportExcel: "/activity/export/excel",
} as const;

export interface DashboardDataWithWeekly extends DashboardData2 {
	weeklyUserInteractions: UserInteractionData[];
	weeklyAskKuralRatings: RatingsData[];
	weeklyReflectiveModeRatings: RatingsData[];
	weeklyFeedbackRatings: RatingsData[];
	weeklyUserActivity: UserActivityData[];
	weeklyScreenTime: ScreenTimeData[];
}

// ============ API RESPONSE WRAPPERS ============

export interface ApiResponse<T> {
	data: T;
	status?: number;
	message?: string;
}

export interface DashboardV2Response {
	metadata?: {
		availableGranularities?: string[];
		[key: string]: unknown;
	};
	data?: {
		metadata?: {
			availableGranularities?: string[];
			[key: string]: unknown;
		};
		day?: APIGranularityData;
		week?: APIGranularityData;
		month?: APIGranularityData;
	};
	day?: APIGranularityData;
	week?: APIGranularityData;
	month?: APIGranularityData;
}

export interface ExportResponse {
	data: {
		url: string;
		file_name?: string;
	};
}

export interface PieChartTooltipParams {
	name: string;
	value: number;
	percent: number;
	marker?: string;
	seriesName?: string;
}

// interface DashboardPayload {
// 	time_period: string;
// 	granularity: string;
// 	profile: string;
// 	user_emails?: string;
// }

// ============ PEAK USAGE — API RESPONSE INTERFACES ============

export type APIPeakUsageChartMode = "hourly_per_day" | "weekly_daily" | "monthly_weekly";

// chartMode: "weekly_daily" → last_4_weeks, last_3_months
export interface APIPeakUsageWeekEntry {
	week: string; // "02/02-02/08"
	weekKey: string; // "2026-W06"
	totalQueries: number;
	avgQueriesPerDay: number;
	peakDay: string;
	peakDayQueries: number;
	days: { date: string; totalQueries: number }[];
}

// chartMode: "monthly_weekly" → 6_months, this_year, till_date
export interface APIPeakUsageMonthEntry {
	month: string; // "2026-02"
	totalQueries: number;
	avgQueriesPerWeek: number;
	peakWeek: string | null;
	peakWeekQueries: number;
	weeks: { week: string; weekKey: string; totalQueries: number }[];
}

// dailyData entries inside weekly_daily responses
export interface APIPeakUsageHourlyDay {
	date: string;
	totalQueries: number;
	peakHour: string | null;
	peakHourQueries: number;
	hours: number[]; // always length 24
}

// summary shapes differ per chartMode
export interface APIPeakUsageSummaryHourly {
	peakHour: string;
	totalDaysInPeriod: number;
	avgQueriesPerDay: number;
	avgHours: number[]; // length 24
}

export interface APIPeakUsageSummaryWeeklyDaily {
	peakWeek: string;
	peakWeekQueries: number;
	totalWeeksInPeriod: number;
	avgQueriesPerWeek: number;
	totalDaysInPeriod: number;
	avgQueriesPerDay: number;
	peakDay: string;
	peakDayQueries: number;
}

export interface APIPeakUsageSummaryMonthlyWeekly {
	peakMonth: string;
	peakMonthQueries: number;
	totalMonthsInPeriod: number;
	avgQueriesPerMonth: number;
}

export type APIPeakUsageSummary = APIPeakUsageSummaryHourly | APIPeakUsageSummaryWeeklyDaily | APIPeakUsageSummaryMonthlyWeekly;

export interface APIPeakUsageMetadata {
	timePeriod: string;
	chartMode: APIPeakUsageChartMode;
	startDate: string;
	endDate: string;
	profileFilter: string;
	userEmailFilter: string;
	queryTimeMs: number;
	requestedAt: string;
	note: string;
}

export interface APIPeakUsageResponse {
	data: {
		peakUsage: APIPeakUsageWeekEntry[] | APIPeakUsageMonthEntry[] | APIPeakUsageHourlyDay[];
		dailyData?: APIPeakUsageHourlyDay[];
		summary: APIPeakUsageSummary;
		metadata: APIPeakUsageMetadata;
	};
	message: string;
	error: string | null;
}

// ============ PEAK USAGE — NORMALIZED INTERFACES ============

export type PeakUsageGranularity = "daily" | "weekly" | "monthly";

export interface PeakUsageHourlyPoint {
	label: string; // "15:00"
	date: string; // "2026-02-04" — for date boundary rendering
	hour: number; // 0–23
	queries: number;
	isFirstHourOfDay: boolean;
}

export interface PeakUsageWeeklyPoint {
	label: string; // "MM-DD" for weekly_daily mode, "MM/DD-MM/DD" for monthly_weekly
	weekKey: string; // "2026-W06"
	totalQueries: number;
	avgQueriesPerDay?: number;
	date?: string;
}

export interface PeakUsageMonthlyPoint {
	label: string; // "Feb 2026"
	month: string; // "2026-02"
	totalQueries: number;
}

export interface PeakUsageData {
	chartMode: APIPeakUsageChartMode;
	// hourly_per_day: this_week — 24 points from summary.avgHours
	avgHourlyPoints: PeakUsageHourlyPoint[];
	// weekly_daily daily toggle — all days × 24 hours, continuous
	hourlyPoints: PeakUsageHourlyPoint[];
	// weekly_daily daily-level points (one per day, flattened from weeks)
	weeklyPoints: PeakUsageWeeklyPoint[];
	// weekly_daily week-level aggregates
	weeklyAggPoints: PeakUsageWeeklyAggPoint[];
	// hourly_per_day / monthly_weekly daily points
	dailyPoints: PeakUsageHourlyPoint[];
	// whether to show hourly chart for the daily toggle
	showHourlyForDaily: boolean;
	// monthly_weekly monthly toggle
	monthlyPoints: PeakUsageMonthlyPoint[];
	summary: APIPeakUsageSummary;
	metadata: APIPeakUsageMetadata;
}

export interface PeakUsageWeeklyAggPoint {
	label: string; // "02/02-02/08"
	weekKey: string; // "2026-W06"
	totalQueries: number; // sum for the week
	avgQueriesPerDay: number; // avg queries per day for this week
	firstDayIndex: number; // index into weeklyPoints where this week starts
}

export interface ZsUserByJobLevel {
	jobLevel: string;
	count: number;
	percentage: number;
}

// ============ API TIMING DISTRIBUTION — BOX CHART INTERFACES ============

export type ITimingMetricKeyType = "firstToken" | "lastToken" | "apiCompletion";

export type ITimingModeKeyType = "fast" | "reflective";

export type IStudyTypeKeyType = string;
export interface ITimingPeriodEntry {
	period: string; // "02/24-03/02" or "2026-02"
	periodKey: string; // "2026-W09" or "2026-02"
	sampleSize: number;
	metrics: ITimingMetric[];
}
export interface ITimingMetric {
	metric: ITimingMetricKeyType;
	label: string;
	unit: string;
	min: number;
	p5: number;
	q1: number;
	median: number;
	q3: number;
	p95: number;
	max: number;
	mean: number;
	outliers: number[];
	sampleSize: number;
}
export interface ITimingStudyGroup {
	studyType: IStudyTypeKeyType;
	label: string;
	timeSeries: ITimingPeriodEntry[];
	weeklyTimeSeries?: ITimingPeriodEntry[];
	monthlyTimeSeries?: ITimingPeriodEntry[];
	totalSampleSize: number;
}
export interface ITimingModeData {
	groups: ITimingStudyGroup[];
	totalSampleSize: number;
}

export interface IApiTimingDistributionData {
	fast: ITimingModeData;
	reflective: ITimingModeData;
	usageDistribution?: IUsageDistributionData | null;
	availableGranularities?: string[];
}

// ============ USAGE DISTRIBUTION (TOKENS & COST) ============

export type IUsageGranularityType = "daily" | "weekly" | "monthly";

export interface IUsagePeriodEntry {
	period: string;
	periodKey: string;
	costUsd: number;
	totalInputTokens: number;
	totalOutputTokens: number;
}

export interface IUsageByModelEntry {
	model: string;
	totalCostUsd: number;
	totalInputTokens: number;
	totalOutputTokens: number;
	timeSeries?: IUsagePeriodEntry[];
	dailyTimeSeries?: IUsagePeriodEntry[];
	weeklyTimeSeries?: IUsagePeriodEntry[];
	monthlyTimeSeries?: IUsagePeriodEntry[];
}

export interface IUsageTotals {
	totalCostUsd: number;
	totalInputTokens: number;
	totalOutputTokens: number;
}

export interface IUsageDistributionData {
	byModel: IUsageByModelEntry[];
	totals: IUsageTotals;
}

export interface IUsageAggregatedPoint {
	period: string;
	periodKey: string;
	inputTokens: number;
	outputTokens: number;
	cost: number;
}

export interface IApiTimingDistributionResponse {
	data: {
		distribution: {
			apiTimingDistribution: IApiTimingDistributionData;
			usageDistribution?: IUsageDistributionData | null;
			granularity?: string[];
			dataAvailableFrom?: string;
			totalRows?: number;
		};
		metadata?: {
			time_period: string;
			granularity?: string | string[];
			from_cache: boolean;
			query_time_ms: number;
			filters: Record<string, string>;
		};
	};
	metadata?: {
		time_period: string;
		granularity?: string | string[];
		from_cache: boolean;
		query_time_ms: number;
		filters: Record<string, string>;
	};
	message?: string;
	error?: string | null;
}

export interface IRecentActiveUser {
	collaborator_id: string;
	email: string;
	name: string;
	enable_access: boolean;
	last_active_date: string;
}

export interface IRecentActiveUsersResponse {
	data: {
		users: IRecentActiveUser[];
		total: number;
	};
	error: string | null;
	message: string;
}

// ============ METRICS METADATA INTERFACES ============

export interface IMetricsRefreshState {
	last_successful_run_at: string | null;
	last_run_status: string | null;
	last_run_triggered_by: string | null;
	next_scheduled_run_at: string | null;
	refresh_interval_hours: number;
	metrics_job_status: "idle" | "running";
	norm_cache_warmed_at: string | null;
	rollup_completed_at: string | null;
}

export interface IDateRange {
	start: string | null;
	end: string | null;
}

export interface IMetricsMetadataResponse {
	data: {
		refresh_state: IMetricsRefreshState;
		date_ranges: Record<Duration, IDateRange>;
	};
	message: string;
	error: string | null;
}

// ============ USER MONITORING CACHE INTERFACES ============
export interface ICacheEntry<T = any> {
	data: T;
	cachedAt: number;
}

export interface IUserMonitoringState {
	dashboardCache: Record<string, ICacheEntry>;
	kpiCache: Record<string, ICacheEntry>;
	peakUsageCache: Record<string, ICacheEntry>;
	apiTimingCache: Record<string, ICacheEntry>;
	lastRefreshedAt: string | null;
}
